import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

/**
 * Custom dropdown replacing native <select> — the native option list draws an
 * OS-styled highlight bar that cannot be restyled cross-browser, so every
 * filter control in the app routes through this instead.
 *
 * Props:
 *   value       — controlled value (string)
 *   onChange     — called with the new value string on selection
 *   options      — [{ value: string, label: string }, …]
 *   ariaLabel    — accessible name for the trigger
 *   className    — extra classes merged onto the outermost wrapper
 */
const SelectMenu = ({ value, onChange, options = [], ariaLabel, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  // The label shown on the closed trigger — fall back to the raw value so it
  // never renders blank if the options array changes mid-flight.
  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? value ?? "";

  const isActive = value !== "" && value != null;

  /* ── open / close helpers ─────────────────────────────────── */

  const open = useCallback(() => {
    setIsOpen(true);
    // Highlight whichever option is currently selected so the keyboard
    // starts from a meaningful position.
    const idx = options.findIndex((o) => o.value === value);
    setActiveIdx(idx >= 0 ? idx : 0);
  }, [options, value]);

  const close = useCallback((returnFocus = true) => {
    setIsOpen(false);
    setActiveIdx(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const select = useCallback(
    (val) => {
      onChange?.(val);
      close();
    },
    [onChange, close]
  );

  /* ── click-outside ────────────────────────────────────────── */
  // Mousedown (not click) so it fires before the trigger's own onClick and
  // we can distinguish "user clicked away" from "user re-clicked the trigger".
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        close(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, close]);

  /* ── keyboard ─────────────────────────────────────────────── */

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
        return;
      }

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          close();
          break;
        case "ArrowDown":
          e.preventDefault();
          setActiveIdx((i) => Math.min(i + 1, options.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIdx((i) => Math.max(i - 1, 0));
          break;
        case "Home":
          e.preventDefault();
          setActiveIdx(0);
          break;
        case "End":
          e.preventDefault();
          setActiveIdx(options.length - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (activeIdx >= 0 && activeIdx < options.length) {
            select(options[activeIdx].value);
          }
          break;
        default:
          break;
      }
    },
    [isOpen, open, close, select, options, activeIdx]
  );

  // Scroll the highlighted option into view when it changes.
  useEffect(() => {
    if (!isOpen || activeIdx < 0) return;
    listRef.current
      ?.querySelectorAll('[role="option"]')
      ?.[activeIdx]?.scrollIntoView({ block: "nearest" });
  }, [isOpen, activeIdx]);

  /* ── render ───────────────────────────────────────────────── */

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className={
          "inline-flex items-center gap-1.5 text-xs rounded-lg px-3 py-1.5 " +
          "border outline-none transition-colors focus:outline-none " +
          (isActive
            ? "border-cyan-400 text-cyan-700 bg-white"
            : "border-[#EBECE6] text-gray-600 bg-white focus:border-cyan-400")
        }
      >
        {selectedLabel}
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Menu */}
      {isOpen && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          // Right-aligned: the control lives at the row's trailing edge via
          // ml-auto, so a left-aligned menu would overhang the container.
          className={
            "absolute right-0 mt-1 min-w-full w-max rounded-lg border border-[#EBECE6] " +
            "bg-white py-1 shadow-lg z-[210] max-h-64 overflow-y-auto d5-recents-scroll " +
            "animate-[fadeIn_100ms_ease-out]"
          }
          onKeyDown={handleKeyDown}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            const isHighlighted = i === activeIdx;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  // Prevent the outside-click handler from racing.
                  e.stopPropagation();
                  select(opt.value);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                className={
                  "flex w-full cursor-pointer items-center justify-between gap-3 " +
                  "px-3 py-1.5 text-xs text-left select-none " +
                  (isHighlighted
                    ? "bg-cyan-50 text-cyan-700"
                    : isSelected
                      ? "text-cyan-700"
                      : "text-gray-600") +
                  (isSelected ? " font-medium" : "")
                }
              >
                {opt.label}
                {isSelected && <Check size={12} aria-hidden="true" className="flex-shrink-0" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SelectMenu;
